/**
 * FeedbackListModal.tsx
 * Modal to display all contact messages/feedback with pagination
 */

import React, { useEffect, useState } from 'react';
import { X, Search, MessageSquare, Eye, Mail, Phone, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getApiEndpoint } from '@/config/api';

type FeedbackMessage = {
  id: number;
  name: string;
  email: string;
  mobile: string;
  subject: string;
  message: string;
  preferred_contact: string;
  best_time: string;
  attachURL: string | null;
  created_at: string;
  processed: boolean;
  processed_at: string | null;
  notes: string | null;
  user_code: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onViewFeedback: (feedbackId: number) => void;
};

export default function FeedbackListModal({ open, onClose, onViewFeedback }: Props) {
  const [messages, setMessages] = useState<FeedbackMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<FeedbackMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 2;

  useEffect(() => {
    if (open) {
      fetchMessages();
    }
  }, [open]);

  useEffect(() => {
    filterMessages();
    setCurrentPage(1);
  }, [messages, searchQuery]);

  async function fetchMessages() {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint('/admin/feedback-list'));
      const result = await response.json();

      if (result.success) {
        setMessages(result.data || []);
      } else {
        toast.error(result.message || 'Failed to fetch feedback');
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Error loading feedback');
    } finally {
      setLoading(false);
    }
  }

  function filterMessages() {
    if (!searchQuery.trim()) {
      setFilteredMessages(messages);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = messages.filter(
      (msg) =>
        msg.name.toLowerCase().includes(query) ||
        msg.email?.toLowerCase().includes(query) ||
        msg.mobile?.includes(query) ||
        msg.subject?.toLowerCase().includes(query) ||
        msg.message.toLowerCase().includes(query)
    );
    setFilteredMessages(filtered);
  }

  // Pagination
  const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMessages = filteredMessages.slice(startIndex, endIndex);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Feedback list modal"
      className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(2,6,23,0.75), rgba(7,16,26,0.75)), var(--dashboard-bg, linear-gradient(135deg,#8694a1 0%,#ecfeff 100%))',
          backdropFilter: 'blur(6px)',
        }}
      />

      {/* Modal */}
      <div className="relative w-full max-w-7xl">
        <div className="bg-white rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10 max-h-[85vh] flex flex-col">
          {/* Header */}
          <div
            className="px-6 py-5 flex items-center justify-between gap-4"
            style={{
              backgroundImage: 'linear-gradient(135deg, #14b8a6 0%, #06b6d4 100%)',
              color: 'white',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-semibold leading-tight">Feedback List</h3>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, mobile, subject..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center px-4 py-2 bg-teal-100 text-teal-700 rounded-lg font-semibold">
                {filteredMessages.length} Message{filteredMessages.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Messages List */}
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-gray-500">Loading feedback...</p>
                </div>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Feedback</h3>
                  <p className="text-gray-500">
                    {searchQuery ? 'No messages match your search' : 'No feedback messages yet'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`bg-white border rounded-xl p-5 hover:shadow-md transition-shadow ${
                      msg.processed ? 'border-gray-200 bg-gray-50' : 'border-teal-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Message Icon */}
                      <div className="flex-shrink-0">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          msg.processed ? 'bg-gray-200' : 'bg-gradient-to-br from-teal-500 to-cyan-500'
                        }`}>
                          <span className="text-white text-lg font-bold">
                            {msg.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Message Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900 text-lg">{msg.name}</h3>
                          {msg.processed && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              Processed
                            </span>
                          )}
                          {msg.attachURL && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              Attachment
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                          {msg.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="truncate">{msg.email}</span>
                            </div>
                          )}
                          {msg.mobile && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span>{msg.mobile}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {msg.subject && (
                          <div className="mb-2">
                            <span className="text-sm font-semibold text-gray-700">Subject: </span>
                            <span className="text-sm text-gray-600">{msg.subject}</span>
                          </div>
                        )}

                        <p className="text-sm text-gray-700 line-clamp-2 mb-3">{msg.message}</p>

                        <button
                          onClick={() => onViewFeedback(msg.id)}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredMessages.length > 0 && totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 mt-6">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredMessages.length)} of {filteredMessages.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-lg transition-colors ${
                          page === currentPage
                            ? 'bg-teal-600 text-white'
                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
